import axios from 'axios';
import { auth } from './firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minute timeout for agent queries
});

// Add auth token to all requests
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// CRL API functions
export const crlApi = {
  getStats: () => api.get('/api/crl/stats'),
  getLetters: (params) => api.get('/api/crl/letters', { params }),
  getLetter: (id) => api.get(`/api/crl/letters/${id}`),
  getApplicationLetters: (applicationNumber) => api.get(`/api/crl/applications/${encodeURIComponent(applicationNumber)}/letters`),
  getCompanies: () => api.get('/api/crl/companies'),
  getSubscribers: () => api.get('/api/crl/alerts/subscribers'),
  subscribe: (email) => api.post('/api/crl/alerts/subscribe', { email }),
  unsubscribe: (email) => api.post('/api/crl/alerts/unsubscribe', { email }),
};

// Insider Trading API functions
export const insiderApi = {
  getAccount: () => api.get('/api/insider/account'),
  getPositions: () => api.get('/api/insider/positions'),
  getSignals: (params) => api.get('/api/insider/signals', { params }),
  getModelPerformance: () => api.get('/api/insider/model-performance'),
  getTrades: (params) => api.get('/api/insider/trades', { params }),
  getPerformance: () => api.get('/api/insider/performance'),
  getStats: () => api.get('/api/insider/stats'),
  getStrategyConfig: () => api.get('/api/insider/strategy-config'),
  updateStrategyConfig: (config) => api.put('/api/insider/strategy-config', config),
  getAlertConfigs: () => api.get('/api/insider/alert-configs'),
  createAlertConfig: (config) => api.post('/api/insider/alert-config', config),
  updateAlertConfig: (id, config) => api.put(`/api/insider/alert-config/${id}`, config),
  deleteAlertConfig: (id) => api.delete(`/api/insider/alert-config/${id}`),
};

// Auth/User management API functions
export const authApi = {
  getCurrentUser: () => api.get('/api/auth/user'),
  getUsers: () => api.get('/api/auth/users'),
  createUser: (userData) => api.post('/api/auth/users', userData),
  updateUser: (uid, userData) => api.put(`/api/auth/users/${uid}`, userData),
  deleteUser: (uid) => api.delete(`/api/auth/users/${uid}`),
};

// Monitoring API functions
export const monitoringApi = {
  getPipelineStatus: () => api.get('/api/monitoring/status'),
  getCrlHistory: () => api.get('/api/monitoring/crl-history'),
};

// Admin API functions
export const adminApi = {
  // AI Model Configuration
  getAIModels: () => api.get('/api/admin/ai-models'),
  getAIConfig: () => api.get('/api/admin/ai-config'),
  updateAIConfig: (config) => api.put('/api/admin/ai-config', config),
};

// Multi-Agent Intelligence API functions
export const agentApi = {
  // Ask a question using the intelligence agent
  ask: (data) => api.post('/api/agent/ask', data),

  // Ask about a specific company
  askAboutCompany: (companyId, data) => api.post(`/api/agent/companies/${companyId}/ask`, data),

  // Streaming ask - returns events as agents complete
  askStream: async (data, onEvent, signal) => {
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : null;

    const response = await fetch(`${API_BASE_URL}/api/agent/ask/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(data),
      signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const eventData = JSON.parse(line.substring(6));
              if (onEvent) {
                onEvent(eventData);
              }
            } catch (e) {
              console.error('Failed to parse SSE event:', e);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },

  // Get agent system info
  getInfo: () => api.get('/api/agent/info'),

  // Conversation management
  getConversation: (conversationId) => api.get(`/api/agent/conversation/${conversationId}`),
  endConversation: (conversationId) => api.delete(`/api/agent/conversation/${conversationId}`),

  // A2A protocol - agent discovery
  getAgentCard: () => api.get('/.well-known/agent.json'),
};

// Company Intelligence API functions
export const companyApi = {
  // Companies
  getCompanies: (params) => api.get('/api/company/companies', { params }),
  getCompany: (companyId) => api.get(`/api/company/companies/${companyId}`),

  // Events
  getCompanyEvents: (companyId, params) => api.get(`/api/company/companies/${companyId}/events`, { params }),
  getAllEvents: (params) => api.get('/api/company/events', { params }),

  // Pipeline
  getCompanyPipeline: (companyId, params) => api.get(`/api/company/companies/${companyId}/pipeline`, { params }),

  // Clinical Trials
  getCompanyTrials: (companyId, params) => api.get(`/api/company/companies/${companyId}/trials`, { params }),

  // Financials
  getCompanyFinancials: (companyId, params) => api.get(`/api/company/companies/${companyId}/financials`, { params }),

  // Insider Transactions
  getCompanyInsider: (companyId, params) => api.get(`/api/company/companies/${companyId}/insider`, { params }),
  getRecentInsider: (params) => api.get('/api/company/insider/recent', { params }),

  // Event Taxonomy
  getEventTaxonomy: () => api.get('/api/company/event-taxonomy'),

  // Q&A (RAG-powered)
  ask: (data) => api.post('/api/company/ask', data),
  askAboutCompany: (companyId, data) => api.post(`/api/company/companies/${companyId}/ask`, data),
  getCompanySummary: (companyId, params) => api.get(`/api/company/companies/${companyId}/summary`, { params }),

  // Search
  search: (params) => api.get('/api/company/search', { params }),

  // Visualization
  generateChart: (data) => api.post('/api/company/visualize', data),
  generateCompanyChart: (companyId, data) => api.post(`/api/company/companies/${companyId}/visualize`, data),

  // Document Generation
  generateDocument: (data) => api.post('/api/company/documents/generate', data),
  generateCompanyDocument: (companyId, data) => api.post(`/api/company/companies/${companyId}/documents/generate`, data),
};

export default api;
